/**
 * Shared progress UI for long-running server tasks (git sync, scans, etc.).
 */
import { Loader2 } from "lucide-react";

interface Props {
  title: string;
  phase: string | null;
  lines: string[];
  error?: string | null;
  progress?: { current: number; total: number } | null;
}

export const ActivityPanel: React.FC<Props> = ({
  title,
  phase,
  lines,
  error,
  progress,
}) => {
  if (!phase && lines.length === 0 && !error) return null;

  const pct =
    progress && progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : null;

  return (
    <div
      className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/80 p-3 text-xs dark:border-indigo-900 dark:bg-indigo-950/50"
      role="status"
      aria-live="polite"
    >
      <div className="mb-2 flex items-center gap-2 font-semibold text-indigo-900 dark:text-indigo-100">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        {title}
      </div>
      {phase && (
        <p className="mb-2 text-indigo-800 dark:text-indigo-200">{phase}</p>
      )}
      {pct != null && (
        <div className="mb-2">
          <div className="mb-1 flex justify-between text-[10px] text-gray-600 dark:text-gray-400">
            <span>
              {progress!.current} / {progress!.total}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-indigo-200 dark:bg-indigo-900">
            <div
              className="h-full rounded-full bg-indigo-600 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
      {error && (
        <p className="mb-2 text-red-600 dark:text-red-400">{error}</p>
      )}
      {lines.length > 0 && (
        <ul className="max-h-32 space-y-0.5 overflow-y-auto font-mono text-[11px] text-gray-700 dark:text-gray-300">
          {lines.slice(-12).map((line, i) => (
            <li key={`${i}-${line}`}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
